import * as THREE from 'three'

import { TILE_SIZE } from '../shared/shared'

const PAD = 1

export class Atlas {
  constructor(client, options = {}) {
    this.client = client
    if (typeof options === 'number') {
      options = { atlasW: options }
    }
    this.sourceProvider = options.sourceProvider || null
    this.atlasW = options.atlasW || 1024
    this.atlasH = 1024
    this._nextY = 0
    this._entries = new Map()
    this._loading = new Map()
    this._initCanvas()
  }

  _initCanvas() {
    this.canvas = document.createElement('canvas')
    this.canvas.width = this.atlasW
    this.canvas.height = this.atlasH
    this.ctx = this.canvas.getContext('2d', { willReadFrequently: true })
    this.ctx.imageSmoothingEnabled = false

    this.texture = new THREE.CanvasTexture(this.canvas)
    this.texture.magFilter = THREE.NearestFilter
    this.texture.minFilter = THREE.NearestFilter
    this.texture.generateMipmaps = false
    this.texture.colorSpace = THREE.LinearSRGBColorSpace
  }

  resolve(url, widthCells = 1, heightCells = 1) {
    const existing = this._entries.get(url)
    if (existing) return existing

    const providedSource = this.sourceProvider?.getSource(url) || null
    const entry = providedSource
      ? {
          url,
          x: null,
          y: null,
          widthPx: providedSource.widthPx,
          heightPx: providedSource.heightPx,
          widthCells,
          heightCells,
          frameCount: 1,
          ready: false,
        }
      : this._allocate(url, widthCells, heightCells)
    this._entries.set(url, entry)

    if (!this._loading.has(url)) {
      const promise = providedSource
        ? this.sourceProvider
            .renderInto(url, source => {
              this._blitVirtual(url, source)
            })
            .then(() => {
              this._loading.delete(url)
            })
            .catch(err => {
              console.warn(`Atlas: failed to compose ${url}`, err)
              this._loading.delete(url)
            })
        : this.client.loader
            .load(url)
            .then(img => {
              this._blit(url, img)
              this._loading.delete(url)
            })
            .catch(err => {
              console.warn(`Atlas: failed to load ${url}`, err)
              this._loading.delete(url)
            })
      this._loading.set(url, promise)
    }

    return entry
  }

  getUV(url) {
    const e = this._entries.get(url)
    if (!e) return null
    if (!e.ready || e.x == null || e.y == null) return null

    const texW = this.canvas.width
    const texH = this.canvas.height
    const px = e.x + PAD
    const py = e.y + PAD
    const pitchPx = e.widthPx + PAD * 2
    const u = px / texW
    const v = 1 - (py + e.heightPx) / texH
    const frameW = e.widthPx / texW
    const frameH = e.heightPx / texH
    const strideU = pitchPx / texW

    return { u, v, frameW, frameH, strideU, frameCount: e.frameCount }
  }

  _allocate(url, widthCells, heightCells) {
    const widthPx = widthCells * TILE_SIZE
    const heightPx = heightCells * TILE_SIZE
    return this._allocateFullRowPixels(url, widthPx, heightPx, widthCells, heightCells)
  }

  _allocateFullRowPixels(url, widthPx, heightPx, widthCells = widthPx / TILE_SIZE, heightCells = heightPx / TILE_SIZE) {
    const stripH = heightPx + PAD * 2
    const y = Math.max(this._nextY, this._packedBottom || 0)
    this._nextY = y + stripH

    if (this._nextY > this.atlasH) {
      this._grow(this._nextY)
    }

    return { url, x: 0, y, widthPx, heightPx, widthCells, heightCells, frameCount: 1, ready: false }
  }

  _allocatePackedPixels(url, widthPx, heightPx, frameCount, widthCells = widthPx / TILE_SIZE, heightCells = heightPx / TILE_SIZE) {
    const stripH = heightPx + PAD * 2
    const pitchPx = widthPx + PAD * 2
    const totalW = frameCount * pitchPx

    if (totalW > this.atlasW) {
      return this._allocateFullRowPixels(url, widthPx, heightPx, widthCells, heightCells)
    }

    let packedX = this._packedX || 0
    let packedY = this._packedY || 0
    let packedRowH = this._packedRowH || 0

    if (packedY < this._nextY) {
      packedX = 0
      packedY = this._nextY
      packedRowH = 0
    }

    if (packedX + totalW > this.atlasW) {
      packedX = 0
      packedY += packedRowH
      packedRowH = 0
      if (packedY < this._nextY) packedY = this._nextY
    }

    const requiredBottom = packedY + stripH
    if (requiredBottom > this.atlasH) {
      this._grow(requiredBottom)
    }

    const entry = { url, x: packedX, y: packedY, widthPx, heightPx, widthCells, heightCells, frameCount, ready: false }
    this._packedX = packedX + totalW
    this._packedY = packedY
    this._packedRowH = Math.max(packedRowH, stripH)
    this._packedBottom = this._packedY + this._packedRowH
    return entry
  }

  _grow(minH) {
    let newH = this.atlasH
    while (newH < minH) newH *= 2
    if (newH === this.atlasH) return
    const oldCanvas = this.canvas
    this.atlasH = newH
    this.canvas = document.createElement('canvas')
    this.canvas.width = this.atlasW
    this.canvas.height = this.atlasH
    this.ctx = this.canvas.getContext('2d', { willReadFrequently: true })
    this.ctx.imageSmoothingEnabled = false
    this.ctx.drawImage(oldCanvas, 0, 0)

    this.texture.dispose()
    this.texture = new THREE.CanvasTexture(this.canvas)
    this.texture.magFilter = THREE.NearestFilter
    this.texture.minFilter = THREE.NearestFilter
    this.texture.generateMipmaps = false
    this.texture.colorSpace = THREE.LinearSRGBColorSpace
    this._textureVersion = (this._textureVersion || 0) + 1
  }

  _blit(url, img) {
    const entry = this._entries.get(url)
    if (!entry) return

    const { widthPx, heightPx } = entry
    const frameCount = Math.max(1, Math.floor(img.width / widthPx))
    entry.frameCount = frameCount

    const pitchPx = widthPx + PAD * 2
    const totalW = frameCount * pitchPx
    if (totalW > this.atlasW) {
      console.warn(`Atlas: ${url} needs ${totalW}px but atlas is ${this.atlasW}px wide`)
    }

    for (let f = 0; f < frameCount; f++) {
      const srcX = f * widthPx
      const dstX = entry.x + f * pitchPx + PAD
      const dstY = entry.y + PAD
      this.ctx.clearRect(dstX - PAD, dstY - PAD, pitchPx, heightPx + PAD * 2)
      this.ctx.drawImage(img, srcX, 0, widthPx, heightPx, dstX, dstY, widthPx, heightPx)
      this._extrude(dstX, dstY, widthPx, heightPx)
    }

    entry.ready = true
    this.texture.needsUpdate = true
    this._onEntryReady(url)
  }

  _blitVirtual(url, source) {
    let entry = this._entries.get(url)
    if (!entry) return

    if (entry.x == null || entry.y == null) {
      const allocated = this._allocatePackedPixels(
        url,
        source.widthPx,
        source.heightPx,
        source.frameCount || 1,
        entry.widthCells,
        entry.heightCells
      )
      Object.assign(entry, allocated)
    }

    const widthPx = source.widthPx
    const heightPx = source.heightPx
    const frameCount = source.frameCount || 1
    const dstY = entry.y + PAD
    const pitchPx = widthPx + PAD * 2
    const totalW = frameCount * pitchPx
    if (totalW > this.atlasW) {
      console.warn(`Atlas: ${url} needs ${totalW}px but atlas is ${this.atlasW}px wide`)
    }

    for (let f = 0; f < frameCount; f++) {
      const srcX = f * widthPx
      const frameDstX = entry.x + f * pitchPx + PAD
      this.ctx.clearRect(frameDstX - PAD, dstY - PAD, widthPx + PAD * 2, heightPx + PAD * 2)
      this.ctx.drawImage(source.canvas, srcX, 0, widthPx, heightPx, frameDstX, dstY, widthPx, heightPx)
      this._extrude(frameDstX, dstY, widthPx, heightPx)
    }

    entry.frameCount = frameCount
    entry.ready = true
    this.texture.needsUpdate = true
    this._onEntryReady(url)
  }

  _extrude(ox, oy, w, h) {
    const c = this.canvas
    const x = this.ctx
    x.drawImage(c, ox, oy, w, 1, ox, oy - PAD, w, PAD)
    x.drawImage(c, ox, oy + h - 1, w, 1, ox, oy + h, w, PAD)
    x.drawImage(c, ox, oy, 1, h, ox - PAD, oy, PAD, h)
    x.drawImage(c, ox + w - 1, oy, 1, h, ox + w, oy, PAD, h)
    x.drawImage(c, ox, oy, 1, 1, ox - PAD, oy - PAD, PAD, PAD)
    x.drawImage(c, ox + w - 1, oy, 1, 1, ox + w, oy - PAD, PAD, PAD)
    x.drawImage(c, ox, oy + h - 1, 1, 1, ox - PAD, oy + h, PAD, PAD)
    x.drawImage(c, ox + w - 1, oy + h - 1, 1, 1, ox + w, oy + h, PAD, PAD)
  }

  _listeners = []

  onReady(fn) {
    this._listeners.push(fn)
  }

  offReady(fn) {
    const idx = this._listeners.indexOf(fn)
    if (idx !== -1) this._listeners.splice(idx, 1)
  }

  _onEntryReady(url) {
    for (const fn of this._listeners) {
      fn(url)
    }
  }
}
